/* =============================================================================
   REDEM MILANO — animations.js
   Animazioni di rivelazione al scroll tramite IntersectionObserver.
   Rispetta prefers-reduced-motion per garantire l'accessibilità.
   Vanilla ES6+, niente framework.
   ============================================================================= */

'use strict';

/* -----------------------------------------------------------------------------
   CONTROLLO PREFERENZE MOVIMENTO
   Se l'utente ha scelto di ridurre le animazioni nel sistema operativo,
   il modulo non collega alcun observer e lascia tutti gli elementi visibili.
   ----------------------------------------------------------------------------- */

/** @type {boolean} true se l'utente preferisce ridurre le animazioni */
const _movimentoRidotto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -----------------------------------------------------------------------------
   INTERSECTION OBSERVER — elementi .reveal
   Aggiunge la classe is-visible quando un elemento entra nel viewport.
   ----------------------------------------------------------------------------- */
(function inizializzaReveal() {
  // Se l'utente preferisce ridurre il movimento, rendi subito visibili tutti gli elementi
  if (_movimentoRidotto) {
    document.querySelectorAll('.reveal, .reveal-stagger > *').forEach((el) => {
      el.classList.add('is-visible');
    });
    return;
  }

  // Verifica supporto nativo IntersectionObserver
  if (!('IntersectionObserver' in window)) {
    // Fallback: mostra tutto immediatamente su browser datati
    document.querySelectorAll('.reveal, .reveal-stagger > *').forEach((el) => {
      el.classList.add('is-visible');
    });
    return;
  }

  /* -------------------------------------------------------------------------
     Configurazione observer
     - threshold 0.1: si attiva quando almeno il 10% dell'elemento è visibile
     - rootMargin: lieve margine negativo in basso per ritardare l'attivazione
     ----------------------------------------------------------------------- */
  const opzioniObserver = {
    threshold:  0.1,
    rootMargin: '0px 0px -40px 0px',
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      // Aggiunge la classe che avvia la transizione CSS
      entry.target.classList.add('is-visible');

      // Smette di osservare l'elemento: l'animazione avviene una sola volta
      observer.unobserve(entry.target);
    });
  }, opzioniObserver);

  /* -------------------------------------------------------------------------
     Osserva tutti gli elementi .reveal nella pagina
     ----------------------------------------------------------------------- */
  document.querySelectorAll('.reveal').forEach((el) => {
    observer.observe(el);
  });

  /* -------------------------------------------------------------------------
     STAGGER — .reveal-stagger
     I figli diretti di un contenitore .reveal-stagger ricevono un ritardo
     incrementale (100 ms per ogni figlio) così entrano in sequenza.
     ----------------------------------------------------------------------- */
  document.querySelectorAll('.reveal-stagger').forEach((contenitore) => {
    const figli = Array.from(contenitore.children);

    figli.forEach((figlio, indice) => {
      // Applica il ritardo come proprietà CSS personalizzata
      figlio.style.transitionDelay = `${indice * 100}ms`;
      // Osserva ogni figlio singolarmente
      observer.observe(figlio);
    });
  });
})();

/* -----------------------------------------------------------------------------
   HERO — animazione fade-in-up al caricamento della pagina
   L'intestazione e il sottotitolo dell'hero si animano subito al caricamento,
   indipendentemente dallo scroll (non usa IntersectionObserver).
   ----------------------------------------------------------------------------- */
(function inizializzaAnimazioneHero() {
  /**
   * Applica l'animazione fade-in-up su un elemento con un ritardo specificato.
   * Se prefers-reduced-motion è attivo, mostra l'elemento subito senza animazione.
   * @param {HTMLElement} el      - Elemento da animare
   * @param {number}      ritardo - Ritardo in millisecondi prima dell'avvio
   */
  function animaElementoHero(el, ritardo) {
    if (!el) return;

    if (_movimentoRidotto) {
      // Rendi visibile immediatamente senza animazione
      el.style.opacity   = '1';
      el.style.transform = 'none';
      return;
    }

    // Stato iniziale: invisibile e spostato verso il basso
    el.style.opacity    = '0';
    el.style.transform  = 'translateY(28px)';
    el.style.transition = `opacity 0.8s ease ${ritardo}ms, transform 0.8s ease ${ritardo}ms`;
    el.style.willChange = 'opacity, transform';

    // Piccolo ritardo per garantire che la transizione si attivi correttamente
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity   = '1';
        el.style.transform = 'translateY(0)';
      });
    });

    // Pulizia: rimuovi will-change dopo il completamento dell'animazione
    el.addEventListener(
      'transitionend',
      () => { el.style.willChange = 'auto'; },
      { once: true }
    );
  }

  function avviaAnimazioniHero() {
    const titolo     = document.querySelector('.hero__heading');
    const sottotitolo = document.querySelector('.hero__subheading');

    // Il titolo parte subito, il sottotitolo con 180 ms di ritardo
    animaElementoHero(titolo,      0);
    animaElementoHero(sottotitolo, 180);

    // Anima anche eventuali CTA dell'hero con ulteriore ritardo
    const ctaHero = document.querySelector('.hero__cta');
    animaElementoHero(ctaHero, 360);
  }

  // Avvia le animazioni hero non appena il DOM è pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', avviaAnimazioniHero);
  } else {
    // DOM già pronto (script caricato in defer o in fondo al body)
    avviaAnimazioniHero();
  }
})();

/* -----------------------------------------------------------------------------
   OBSERVER DINAMICO
   Osserva gli elementi .reveal aggiunti dinamicamente al DOM dopo il
   caricamento iniziale (es. prodotti caricati via AJAX / infinite scroll).
   ----------------------------------------------------------------------------- */
(function inizializzaObserverDinamico() {
  if (_movimentoRidotto || !('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

  // Observer di rivelazione condiviso con il modulo principale
  const observerRivela = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observerRivela.unobserve(entry.target);
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );

  // MutationObserver: intercetta nuovi nodi aggiunti al DOM
  const mutationObserver = new MutationObserver((mutazioni) => {
    mutazioni.forEach((mutazione) => {
      mutazione.addedNodes.forEach((nodo) => {
        if (nodo.nodeType !== Node.ELEMENT_NODE) return;

        // Controlla il nodo stesso
        if (nodo.classList.contains('reveal')) {
          observerRivela.observe(nodo);
        }

        // Controlla i discendenti
        nodo.querySelectorAll('.reveal').forEach((el) => {
          observerRivela.observe(el);
        });

        // Gestisci eventuali .reveal-stagger aggiunti dinamicamente
        nodo.querySelectorAll('.reveal-stagger').forEach((contenitore) => {
          Array.from(contenitore.children).forEach((figlio, indice) => {
            figlio.style.transitionDelay = `${indice * 100}ms`;
            observerRivela.observe(figlio);
          });
        });
      });
    });
  });

  // Osserva l'intero documento per qualsiasi inserimento nel DOM
  mutationObserver.observe(document.body, {
    childList: true,
    subtree:   true,
  });
})();

/* -----------------------------------------------------------------------------
   SFUMATO REVEAL — la forma emerge da una leggera sfocatura
   Elementi .sfumato-reveal: opacity + blur(2px->0) + y(24px->0), 0.8s.
   IntersectionObserver, threshold 0.15. Coerente con il linguaggio Sfumato.
   ----------------------------------------------------------------------------- */
(function inizializzaSfumatoReveal() {
  const elementi = document.querySelectorAll('.sfumato-reveal');
  if (!elementi.length) return;

  if (_movimentoRidotto || !('IntersectionObserver' in window)) {
    elementi.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  elementi.forEach((el) => observer.observe(el));
})();

/* -----------------------------------------------------------------------------
   PAGE TRANSITIONS — memoria dello sfumato tra le pagine
   Click su link interno -> overlay #1A1A1A 0->0.6 (0.2s), poi navigazione.
   Al ritorno (pageshow / bfcache) l'overlay si ritira.
   ----------------------------------------------------------------------------- */
(function inizializzaPageTransition() {
  if (_movimentoRidotto) return;

  const overlay = document.querySelector('.page-transition');
  if (!overlay) return;

  function isInterno(anchor) {
    if (!anchor) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.dataset.noTransition === 'true') return false;

    const href = anchor.getAttribute('href');
    if (!href || href.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return false;

    // stesso origine, percorso diverso dall'attuale
    return anchor.origin === window.location.origin &&
      anchor.pathname !== window.location.pathname;
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = event.target.closest('a');
    if (!isInterno(anchor)) return;

    event.preventDefault();
    overlay.classList.add('is-active');

    window.setTimeout(() => {
      window.location.href = anchor.href;
    }, 200);
  });

  // Ritiro dell'overlay quando si torna indietro (bfcache)
  window.addEventListener('pageshow', () => {
    overlay.classList.remove('is-active');
  });
})();
